import cn from 'classnames'
import { HTMLMotionProps, motion } from 'framer-motion'
import React, { forwardRef, ReactElement, Ref, useMemo } from 'react'
import Icon from 'core/components/icons/Icon'
import { IconType } from 'core/components/icons/types'

export interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'prefix'> {
  /**
   * @description: Button variant determining preset styles of the button
   */
  variant?: 'default' | 'primary' | 'error' | 'outline' | 'link'
  /**
   * @description: Prefix the button with an element, typically for icon, but can be used for anything
   */
  prefix?: ReactElement
  /**
   * @description: Suffix the button with an element, typically for icon, badges, etc. but can be used for anything
   */
  suffix?: ReactElement
  /**
   * @description: Prevent the button from being clickable
   */
  disabled?: boolean
  /**
   * @description: Async status from a request
   */
  status?: 'loading' | 'success' | 'error' | 'idle'
  /**
   * @description: Icon name as a default prefix
   */
  icon?: `${IconType}`
}

/**
 * @description: Basic button for user interaction.
 */
const Button = forwardRef(
  (
    {
      className = '',
      children,
      variant = 'default',
      prefix,
      suffix,
      type = 'button',
      disabled,
      onClick,
      status,
      icon,
      ...props
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const variantStyles: Record<string, string> = useMemo(
      () => ({
        default:
          'bg-white hover:bg-dark-1 outline-transparent disabled:bg-dark-1',
        primary:
          'hover:bg-primary-3 bg-primary-0 text-white outline-transparent disabled:bg-dark-1',
        error:
          'hover:bg-danger-5 bg-danger-3 text-white outline-transparent disabled:bg-dark-1',
        outline:
          'bg-white outline-dark-2 focus:outline-primary focus:outline  hover:outline-primary-0 hover:text-primary-0 text-dark-4 ',
        link: 'bg-transparent outline-transparent hover:opacity-75 disabled:!bg-transparent'
      }),
      []
    )

    const iconStyles: Record<string, string> = useMemo(
      () => ({
        primary: '[&>svg]:text-white [&>div>svg]:text-white',
        outline:
          'group-hover:[&>svg]:text-primary group-hover:[&>div>svg]:text-primary'
      }),
      []
    )

    const isIconButton = !children && (prefix || suffix)

    const asyncPrefixIcon = useMemo(() => {
      if ((status === 'idle' && icon) || (!children && icon)) {
        return <Icon name={icon} size={14} className="mb-px" noWrapper />
      }
      if (status === 'loading') {
        return <Icon name="loading" size={14} className="mb-[3px]" noWrapper />
      }
      if (status === 'success') {
        return (
          <Icon
            name="check-circle-outline"
            size={14}
            className="mb-[2px]"
            noWrapper
          />
        )
      }
      if (status === 'error') {
        return (
          <Icon
            name="close-circle-outline"
            size={14}
            className="mb-[2px]"
            noWrapper
          />
        )
      }

      return null
    }, [status, icon, children])

    return (
      <motion.button
        className={cn([
          'inline-block whitespace-nowrap group font-medium rounded-md outline !outline-1 transition-colors duration-300 group',
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:!text-dark-7 disabled:hover:shadow-none',
          variantStyles[variant],
          isIconButton
            ? 'px-2.5 py-2'
            : variant === 'link'
              ? 'p-0'
              : 'px-4 py-2',
          className,
          'disabled:pointer-events-none disabled:cursor-not-allowed disabled:!text-dark-7 disabled:hover:shadow-none'
        ])}
        type={type}
        disabled={disabled}
        ref={ref}
        onClick={(e) => {
          e.currentTarget.blur()
          if (status === 'loading') return
          onClick?.(e)
        }}
        {...props}
      >
        <>
          {prefix && !asyncPrefixIcon && (
            <span className={cn(children && 'mr-2', iconStyles[variant] ?? '')}>
              {prefix}
            </span>
          )}
          {asyncPrefixIcon && (
            <span className={cn(children && 'mr-2', iconStyles[variant] ?? '')}>
              {asyncPrefixIcon}
            </span>
          )}
          {children}
          {suffix && (
            <span className={cn(children && 'ml-2', iconStyles[variant] ?? '')}>
              {suffix}
            </span>
          )}
        </>
      </motion.button>
    )
  }
)

export default Button
